import { useMemo } from 'react';
import { diffWords, diffLines } from 'diff';
import { AnyEntityVersion } from '../types';

export interface AttributeDiff {
  key: string;
  oldValue: unknown;
  newValue: unknown;
  hasChanges: boolean;
  isNested: boolean;
}

export interface TextDiffPart {
  value: string;
  added?: boolean;
  removed?: boolean;
}

/**
 * Checks if a value is a plain object (not array, null, or primitive)
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

/**
 * Deep equality check for two values
 */
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => deepEqual(a[key], b[key]));
  }

  return false;
};

/**
 * Format a value for display in diff
 */
export const formatValueForDiff = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
};

/**
 * Compute text diff between two string values
 */
export const computeTextDiff = (oldValue: string, newValue: string): TextDiffPart[] => {
  const changes = diffWords(oldValue, newValue);
  return changes.map((part) => ({
    value: part.value,
    added: part.added,
    removed: part.removed,
  }));
};

/**
 * Compute line-by-line diff for JSON/multiline content
 */
export const computeLineDiff = (
  oldValue: string,
  newValue: string,
): TextDiffPart[] => {
  const changes = diffLines(oldValue, newValue);
  return changes.map((part) => ({
    value: part.value,
    added: part.added,
    removed: part.removed,
  }));
};

/**
 * Compute differences between two entity versions
 */
export const computeVersionDiff = (
  currentVersion: Record<string, unknown>,
  previousVersion: Record<string, unknown>,
  showUnchanged: boolean = false,
): AttributeDiff[] => {
  // Get all unique keys from both versions
  const allKeys = new Set([
    ...Object.keys(currentVersion),
    ...Object.keys(previousVersion),
  ]);

  // Keys to exclude from comparison (metadata)
  const excludeKeys = new Set(['updateId', 'updatedAt', 'createdAt']);

  const diffs: AttributeDiff[] = [];

  allKeys.forEach((key) => {
    if (excludeKeys.has(key)) return;

    const oldValue = previousVersion[key];
    const newValue = currentVersion[key];
    const hasChanges = !deepEqual(oldValue, newValue);
    const isNested = isPlainObject(oldValue) || isPlainObject(newValue)
      || Array.isArray(oldValue) || Array.isArray(newValue);

    if (hasChanges || showUnchanged) {
      diffs.push({
        key,
        oldValue,
        newValue,
        hasChanges,
        isNested,
      });
    }
  });

  // Sort: alphabetically by key
  return diffs.sort((a, b) => a.key.localeCompare(b.key));
};

/**
 * Compute differences between two AnyEntityVersion objects.
 * Convenience wrapper that handles the type cast from AnyEntityVersion to Record.
 */
export const computeEntityVersionDiff = (
  currentVersion: AnyEntityVersion,
  previousVersion: AnyEntityVersion,
  showUnchanged: boolean = false,
): AttributeDiff[] => computeVersionDiff(
  currentVersion as unknown as Record<string, unknown>,
  previousVersion as unknown as Record<string, unknown>,
  showUnchanged,
);

/**
 * Hook to compute differences between two entity versions
 */
export const useVersionDiff = (
  currentVersion: AnyEntityVersion | null,
  previousVersion: AnyEntityVersion | null,
  showUnchanged: boolean = false,
): AttributeDiff[] => useMemo(() => {
  if (!currentVersion || !previousVersion) {
    return [];
  }

  // Cast to record type for dynamic key access
  const prevRecord = previousVersion as unknown as Record<string, unknown>;
  const currRecord = currentVersion as unknown as Record<string, unknown>;

  return computeVersionDiff(currRecord, prevRecord, showUnchanged);
}, [currentVersion, previousVersion, showUnchanged]);

export default useVersionDiff;
